import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { InboxRow, useMarkRead, useUnread } from '../components/InboxMenu';
import { useGo } from '../components/Nav';
import { Button, EmptyState, ErrorState, Loading, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { qk } from '../queries/keys';
import { fetchInbox, type InboxItem } from '../queries/inbox';

/**
 * Everything that happened to your recordings, newest first, with the
 * unread ones marked. The bell shows the last few; this is all of them.
 */
export function InboxPage() {
  const go = useGo();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const unread = useUnread();
  const read = useMarkRead();
  const query = useInfiniteQuery({
    queryKey: qk.inbox(unreadOnly),
    queryFn: ({ pageParam }) => fetchInbox({ cursor: pageParam, limit: 30, unreadOnly }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  useEffect(() => {
    if (unread.data) track('Inbox Opened', { unread: unread.data.unread });
    // Once, with whatever the badge said when the page opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread.data !== undefined]);

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const count = unread.data?.unread ?? 0;

  const openItem = (item: InboxItem) => {
    track('Inbox Item Opened', { event: item.event });
    if (item.readAt === null) read.mutate([item.id]);
    if (!item.videoGone) go(item.url);
  };

  return (
    <>
      <PageHeader
        title="Inbox"
        subtitle="Who watched, reacted and commented on your recordings."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setUnreadOnly((value) => !value)}
              aria-pressed={unreadOnly}
              className={cx(
                'h-8 rounded-full border px-3.5 text-caption transition-colors duration-120',
                unreadOnly ? 'border-signal/60 bg-signal/10 text-chalk' : 'border-hairline bg-slate-850 text-chalk-dim hover:text-chalk',
              )}
            >
              Unread only
            </button>
            <Button size="sm" disabled={count === 0} busy={read.isPending} onClick={() => read.mutate(undefined)}>
              Mark all read
            </Button>
          </div>
        }
      />

      {query.isPending ? <Loading label="Loading your inbox" /> : null}
      {query.isError ? <ErrorState title="Your inbox did not load" message={errorMessage(query.error)} onRetry={() => void query.refetch()} /> : null}
      {query.data && items.length === 0 ? (
        <EmptyState title={unreadOnly ? 'Nothing unread' : 'Nothing yet'}>
          {unreadOnly ? 'You have seen everything.' : 'When somebody watches, reacts to or comments on one of your recordings, it lands here.'}
        </EmptyState>
      ) : null}
      {items.length > 0 ? (
        <div className="card">
          {items.map((item) => (
            <InboxRow key={item.id} item={item} onOpen={openItem} />
          ))}
        </div>
      ) : null}
      {query.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button onClick={() => void query.fetchNextPage()} busy={query.isFetchingNextPage}>
            Load more
          </Button>
        </div>
      ) : null}
    </>
  );
}
